// Artemision genesis — deploy a fresh ArtemisionMintFT instance on chipnet.
//
// The wizard collects params (ticker, rate, feeBps) and then this module
// builds + broadcasts the genesis transaction that:
//   • Creates a new CashTokens category (id = txid of the tx spending
//     input 0 whose vout=0)
//   • Reservoirs the entire FT supply + a minting NFT at the derived
//     ArtemisionMintFT contract address
//
// After a successful broadcast we cache the params locally so Potidaea's
// swap picker can immediately trade the new asset (see `LOCAL_ASSETS_KEY`
// + loadLocalKnownMbas). Cross-user visibility waits on v2 Katalogos with
// on-chain params — this cache only serves the creator's own sessions.
//
// This is not a wallet backend; it's the mockup's implementation of the
// wizard's "Broadcast" button. Users pay only the miner fee (no founder
// fee on genesis; the founder fee applies to claim() outputs later).

import artifact from './artifacts/ArtemisionMintFT.json';
import shareArtifact from './artifacts/DeviantShareMBA.json';
import type { Artifact } from 'cashscript';
import { Contract, ElectrumNetworkProvider, TransactionBuilder, SignatureTemplate } from 'cashscript';
import type { Signer } from './signer-store';
import type { KnownMba } from './orbital';

const ART = artifact as unknown as Artifact;
const SHARE_ART = shareArtifact as unknown as Artifact;

const RESERVOIR_OUTPUT_SATS = 1000n;
const GENESIS_MINER_FEE = 2000n;
const DUST = 1000n;
const DEFAULT_RESERVOIR_SUPPLY = 10n ** 18n;   // just under CashTokens' 2^63-1 cap

function hexToBin(h: string): Uint8Array {
  const c = h.startsWith('0x') ? h.slice(2) : h;
  const out = new Uint8Array(c.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(c.slice(i * 2, i * 2 + 2), 16);
  return out;
}
function binToHex(b: Uint8Array): string {
  let s = ''; for (const x of b) s += x.toString(16).padStart(2, '0'); return s;
}
function txidLE(hex: string): Uint8Array {
  const d = hexToBin(hex);
  const o = new Uint8Array(d.length);
  for (let i = 0; i < d.length; i++) o[i] = d[d.length - 1 - i];
  return o;
}
function p2pkhLB(pkh: Uint8Array): Uint8Array {
  const o = new Uint8Array(25);
  o[0] = 0x76; o[1] = 0xa9; o[2] = 0x14; o.set(pkh, 3); o[23] = 0x88; o[24] = 0xac;
  return o;
}

export interface GenesisParams {
  ticker: string;
  rateSats: bigint;
  founderFeeBps: bigint;           // 0..500
  reservoirSupply?: bigint;        // default 10^18
  name?: string;                   // display name (cached locally only, not on-chain)
  glyph?: string;                  // display glyph  (cached locally only)
  iconUrl?: string;                // data URL or Sia path (cached locally only)
}

export interface GenesisResult extends KnownMba {
  txid: string;
  name?: string;
  glyph?: string;
  iconUrl?: string;
}

export async function genesisArtemisionMBA(
  signer: Signer,
  params: GenesisParams,
): Promise<GenesisResult> {
  if (params.founderFeeBps < 0n || params.founderFeeBps > 500n) {
    throw new Error('founderFeeBps out of range 0..500');
  }
  const reservoirSupply = params.reservoirSupply ?? DEFAULT_RESERVOIR_SUPPLY;
  const provider = new ElectrumNetworkProvider('chipnet');

  // Pick an eligible funding UTXO — must be vout=0, plain BCH (no token),
  // enough sats for reservoir + miner fee + change.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const own = (await provider.getUtxos(signer.cashaddr)) as any[];
  const eligible = own.find((u) => u.vout === 0 && !u.token && BigInt(u.satoshis) >= RESERVOIR_OUTPUT_SATS + GENESIS_MINER_FEE + DUST);
  if (!eligible) {
    throw new Error(
      'No suitable UTXO for genesis: need a plain BCH UTXO at vout=0 with ' +
      (RESERVOIR_OUTPUT_SATS + GENESIS_MINER_FEE + DUST) + '+ sats. ' +
      'CashTokens genesis requires input 0 to reference an outpoint at index 0.',
    );
  }

  const categoryId: string = eligible.txid;
  const tokenCategory = txidLE(categoryId);
  const founderLB = p2pkhLB(signer.publicKeyHash);
  const founderLockingBytecodeHex = binToHex(founderLB);

  // Contract address is a function of (bytecode, constructor args) — the
  // ArtemisionMintFT constructor takes (tokenCategory, rateSats, feeBps,
  // founderLockingBytecode). Address is deterministic; we compute it now
  // even though the tx isn't broadcast yet.
  const contract = new Contract(ART, [tokenCategory, params.rateSats, params.founderFeeBps, founderLB], { provider });

  const total = BigInt(eligible.satoshis);
  const change = total - RESERVOIR_OUTPUT_SATS - GENESIS_MINER_FEE;
  const sig = new SignatureTemplate(signer.privateKey);

  const tb = new TransactionBuilder({ provider });
  tb.addInput(eligible, sig.unlockP2PKH());
  // Reservoir output — FT supply + minting NFT parked at the contract's
  // token address. Nothing else needs to happen at genesis; claim() will
  // consume this reservoir output later.
  tb.addOutput({
    to: contract.tokenAddress,
    amount: RESERVOIR_OUTPUT_SATS,
    token: {
      amount: reservoirSupply,
      category: categoryId,
      nft: { capability: 'minting', commitment: '' },
    },
  });
  if (change >= DUST) tb.addOutput({ to: signer.cashaddr, amount: change });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sent: any = await tb.send();
  const txid = typeof sent === 'string' ? sent : (sent.txid ?? sent.txId);
  if (!txid) throw new Error('genesis broadcast returned no txid');

  const result: GenesisResult = {
    txid,
    ticker: params.ticker,
    categoryId,
    rateSats: params.rateSats,
    founderFeeBps: params.founderFeeBps,
    founderLockingBytecodeHex,
    contractAddress: contract.address,
    contractTokenAddress: contract.tokenAddress,
    name: params.name,
    glyph: params.glyph,
    iconUrl: params.iconUrl,
  };
  saveLocalAsset(result);
  return result;
}

// ── Share MBA genesis ──────────────────────────────────────────────────────
// Deploy a fresh DeviantShareMBA instance on chipnet. Same reservoir seed
// shape as Artemision (one plain BCH vout=0 input → contract UTXO at
// output 0 carrying FT supply + minting NFT); different constructor
// takes companySplitBps + company/distribution locking bytecodes.

export interface ShareGenesisParams {
  ticker: string;
  rateSats: bigint;
  feeBps: bigint;                       // 0..500 total fee
  companySplitBps: bigint;              // 0..10000 company's share of the fee
  companyLockingBytecodeHex: string;    // 25-byte P2PKH — company treasury
  distributionLockingBytecodeHex: string; // 25-byte P2PKH — distribution vault
  reservoirSupply?: bigint;
  name?: string;
  glyph?: string;
  iconUrl?: string;
}

export interface ShareGenesisResult extends KnownMba {
  txid: string;
  name?: string; glyph?: string; iconUrl?: string;
}

export async function genesisDeviantShareMBA(
  signer: Signer,
  params: ShareGenesisParams,
): Promise<ShareGenesisResult> {
  if (params.feeBps < 0n || params.feeBps > 500n) throw new Error('feeBps out of range 0..500');
  if (params.companySplitBps < 0n || params.companySplitBps > 10000n) throw new Error('companySplitBps out of range 0..10000');
  const reservoirSupply = params.reservoirSupply ?? DEFAULT_RESERVOIR_SUPPLY;
  const provider = new ElectrumNetworkProvider('chipnet');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const own = (await provider.getUtxos(signer.cashaddr)) as any[];
  const eligible = own.find((u) => u.vout === 0 && !u.token && BigInt(u.satoshis) >= RESERVOIR_OUTPUT_SATS + GENESIS_MINER_FEE + DUST);
  if (!eligible) throw new Error(`No suitable UTXO for Share genesis (vout=0 plain BCH with ${RESERVOIR_OUTPUT_SATS + GENESIS_MINER_FEE + DUST}+ sats)`);

  const categoryId: string = eligible.txid;
  const tokenCategory = txidLE(categoryId);
  const companyLB = hexToBin(params.companyLockingBytecodeHex);
  const distributionLB = hexToBin(params.distributionLockingBytecodeHex);

  const contract = new Contract(
    SHARE_ART,
    [tokenCategory, params.rateSats, params.feeBps, params.companySplitBps, companyLB, distributionLB],
    { provider },
  );

  const total = BigInt(eligible.satoshis);
  const change = total - RESERVOIR_OUTPUT_SATS - GENESIS_MINER_FEE;
  const sig = new SignatureTemplate(signer.privateKey);

  const tb = new TransactionBuilder({ provider });
  tb.addInput(eligible, sig.unlockP2PKH());
  tb.addOutput({
    to: contract.tokenAddress,
    amount: RESERVOIR_OUTPUT_SATS,
    token: {
      amount: reservoirSupply,
      category: categoryId,
      nft: { capability: 'minting', commitment: '' },
    },
  });
  if (change >= DUST) tb.addOutput({ to: signer.cashaddr, amount: change });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sent: any = await tb.send();
  const txid = typeof sent === 'string' ? sent : (sent.txid ?? sent.txId);
  if (!txid) throw new Error('Share genesis broadcast returned no txid');

  const result: ShareGenesisResult = {
    txid,
    ticker: params.ticker,
    categoryId,
    templateId: 0x01,
    rateSats: params.rateSats,
    founderFeeBps: params.feeBps,
    founderLockingBytecodeHex: params.companyLockingBytecodeHex,   // company treasury stored in the "founder" slot
    distributionLockingBytecodeHex: params.distributionLockingBytecodeHex,
    companySplitBps: params.companySplitBps,
    contractAddress: contract.address,
    contractTokenAddress: contract.tokenAddress,
    name: params.name,
    glyph: params.glyph,
    iconUrl: params.iconUrl,
  };
  saveLocalAsset(result);
  return result;
}

// ── Local metadata cache ────────────────────────────────────────────────────
// A user who has just created a new asset in this browser should be able to
// SWAP it immediately, without waiting for v2 registry or a mockup rebuild.
// We cache the full descriptor keyed by ticker. Cross-user metadata sharing
// waits for KatalogosRegistryV2.

const LOCAL_ASSETS_KEY = 'deviant.potidaea.local-assets.v1';

interface StoredAsset {
  ticker: string;
  categoryId: string;
  rateSats: string;                // stringified for JSON round-trip
  founderFeeBps: string;
  founderLockingBytecodeHex: string;
  contractAddress: string;
  contractTokenAddress: string;
  txid: string;
  name?: string;
  glyph?: string;
  iconUrl?: string;
  // Share MBA extras (present when templateId === 0x01).
  templateId?: number;
  distributionLockingBytecodeHex?: string;
  companySplitBps?: string;
}

function saveLocalAsset(a: GenesisResult | ShareGenesisResult): void {
  const list = readLocal();
  // Fold duplicates by categoryId — if a user re-runs the wizard with the
  // same ticker but different genesis outpoint, the newer one wins.
  const filtered = list.filter((x) => x.categoryId !== a.categoryId);
  filtered.push({
    ticker: a.ticker,
    categoryId: a.categoryId,
    rateSats: a.rateSats.toString(),
    founderFeeBps: a.founderFeeBps.toString(),
    founderLockingBytecodeHex: a.founderLockingBytecodeHex,
    contractAddress: a.contractAddress,
    contractTokenAddress: a.contractTokenAddress,
    txid: a.txid,
    name: a.name,
    glyph: a.glyph,
    iconUrl: a.iconUrl,
    templateId: a.templateId,
    distributionLockingBytecodeHex: a.distributionLockingBytecodeHex,
    companySplitBps: a.companySplitBps?.toString(),
  });
  try { localStorage.setItem(LOCAL_ASSETS_KEY, JSON.stringify(filtered)); } catch { /* private mode */ }
}

function readLocal(): StoredAsset[] {
  try {
    const raw = localStorage.getItem(LOCAL_ASSETS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch { return []; }
}

/** Rehydrate the locally-created MBAs as KnownMba records so orbital.ts's
 *  swap machinery can trade them directly. Called at boot.
 *  The genesis txid is included as an extra field so the Balances
 *  dashboard can render a "view on chipnet" link per row without a
 *  second lookup. */
export function loadLocalKnownMbas(): Record<string, KnownMba & { name?: string; glyph?: string; iconUrl?: string; genesisTxid?: string }> {
  const out: Record<string, KnownMba & { name?: string; glyph?: string; iconUrl?: string; genesisTxid?: string }> = {};
  for (const s of readLocal()) {
    out[s.ticker] = {
      ticker: s.ticker,
      categoryId: s.categoryId,
      rateSats: BigInt(s.rateSats),
      founderFeeBps: BigInt(s.founderFeeBps),
      founderLockingBytecodeHex: s.founderLockingBytecodeHex,
      contractAddress: s.contractAddress,
      contractTokenAddress: s.contractTokenAddress,
      templateId: s.templateId,
      distributionLockingBytecodeHex: s.distributionLockingBytecodeHex,
      companySplitBps: s.companySplitBps ? BigInt(s.companySplitBps) : undefined,
      name: s.name,
      glyph: s.glyph,
      iconUrl: s.iconUrl,
      genesisTxid: s.txid,
    };
  }
  return out;
}

/** Remove a locally-cached asset by categoryId. */
export function forgetLocalAsset(categoryId: string): void {
  const list = readLocal().filter((x) => x.categoryId !== categoryId);
  try { localStorage.setItem(LOCAL_ASSETS_KEY, JSON.stringify(list)); } catch { /* noop */ }
}

/** Wipe ALL locally-cached MBAs. Used when the user asks to reset the
 *  swap picker to the canonical seed set. On-chain assets are untouched;
 *  only this browser's local memory of them is dropped. */
export function clearAllLocalAssets(): void {
  try { localStorage.removeItem(LOCAL_ASSETS_KEY); } catch { /* noop */ }
}
